// src/pages/tournament/AlgorithmSettings.jsx
// Which ranking and matchmaking method the tournament uses. The choices, what each is good and bad at, and the
// sliders of the methods that have any, all come from the backend's catalog, so there is one place that says
// what a method does. Shown as one line; "Change methods" opens the editor.
import React, { useState } from 'react';

const decimalsOf = (step) => (String(step).split('.')[1] || '').length;

function Slider({ spec, value, onChange }) {
    return (
        <div className="slider">
            <div className="slider-head">
                <span>{spec.label}</span>
                <output>{Number(value).toFixed(decimalsOf(spec.step))}</output>
            </div>
            <input
                type="range"
                aria-label={spec.label}
                min={spec.min}
                max={spec.max}
                step={spec.step}
                value={value}
                onChange={(event) => onChange(Number(event.target.value))}
            />
            <p className="hint">{spec.description}</p>
        </div>
    );
}

const WEIGHT_LABELS = { repeats: 'avoid repeats', skill: 'match skill levels', luck: 'even out luck' };

function MethodPicker({ kind, title, methods, draft, onChange }) {
    const method = methods.find((entry) => entry.id === draft.method);
    const pick = (id) => {
        const next = methods.find((entry) => entry.id === id);
        onChange({ method: id, params: Object.fromEntries(next.params.map((spec) => [spec.key, spec.default])) });
    };
    return (
        <fieldset className="algorithm-picker">
            <legend>{title}</legend>
            <select className="input" aria-label={`${title} method`} value={draft.method} onChange={(event) => pick(event.target.value)}>
                {methods.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                        {entry.name}
                    </option>
                ))}
            </select>
            <p className="algorithm-summary">{method.summary}</p>
            <div className="score-tradeoffs">
                <div>
                    <strong>Good at</strong>
                    <ul>
                        {method.strengths.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                </div>
                <div>
                    <strong>Gives up</strong>
                    <ul>
                        {method.tradeoffs.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                </div>
            </div>
            {method.weights && (
                <p className="hint">
                    Weights:{' '}
                    {Object.keys(WEIGHT_LABELS)
                        .map((key) => `${WEIGHT_LABELS[key]} ${method.weights[key]}`)
                        .join(' · ')}
                </p>
            )}
            {method.params.map((spec) => (
                <Slider
                    key={`${kind}-${draft.method}-${spec.key}`}
                    spec={spec}
                    value={draft.params[spec.key] ?? spec.default}
                    onChange={(value) => onChange({ ...draft, params: { ...draft.params, [spec.key]: value } })}
                />
            ))}
        </fieldset>
    );
}

const sameParams = (a, b) => {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every((key) => Number(a[key]) === Number(b[key]));
};

function AlgorithmSettings({ details, loadCatalog, onSave }) {
    const [editing, setEditing] = useState(false);
    const [catalog, setCatalog] = useState(null);
    const [ranking, setRanking] = useState(null);
    const [matchmaking, setMatchmaking] = useState(null);
    const [saving, setSaving] = useState(false);

    const current = {
        ranking: { method: details.rankingMethod, params: details.rankingParams || {} },
        matchmaking: { method: details.matchmakingMethod, params: details.matchmakingParams || {} },
    };

    const open = async () => {
        setRanking(current.ranking);
        setMatchmaking(current.matchmaking);
        setEditing(true);
        if (!catalog) {
            const loaded = await loadCatalog();
            if (loaded) setCatalog(loaded);
            else setEditing(false); // the failure was reported
        }
    };

    const rankingChanged = ranking && (ranking.method !== current.ranking.method || !sameParams(ranking.params, current.ranking.params));
    const matchmakingChanged =
        matchmaking && (matchmaking.method !== current.matchmaking.method || !sameParams(matchmaking.params, current.matchmaking.params));

    const save = async (event) => {
        event.preventDefault();
        const changes = {};
        if (rankingChanged) Object.assign(changes, { rankingMethod: ranking.method, rankingParams: ranking.params });
        if (matchmakingChanged) Object.assign(changes, { matchmakingMethod: matchmaking.method, matchmakingParams: matchmaking.params });
        setSaving(true);
        const saved = await onSave(changes);
        setSaving(false);
        if (saved) setEditing(false);
    };

    if (!editing) {
        return (
            <section className="card details-card" aria-label="Ranking and matchmaking">
                <div className="details-summary">
                    <ul className="details-facts">
                        <li>
                            <span className="label">Ranking</span> {details.rankingName || details.rankingMethod}
                        </li>
                        <li>
                            <span className="label">Matchmaking</span> {details.matchmakingName || details.matchmakingMethod}
                        </li>
                    </ul>
                    <button className="btn btn-sm" onClick={open}>
                        Change methods
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="card details-card" aria-label="Ranking and matchmaking">
            {!catalog ? (
                <div className="skeleton" aria-busy="true" />
            ) : (
                <form onSubmit={save}>
                    <p className="hint">
                        The ranking method decides how results become a ranking (it can be changed at any time and re-ranks
                        everybody). The matchmaking method decides how the teams of the next rounds are chosen.
                    </p>
                    <MethodPicker kind="ranking" title="Ranking" methods={catalog.ranking} draft={ranking} onChange={setRanking} />
                    <MethodPicker
                        kind="matchmaking"
                        title="Matchmaking"
                        methods={catalog.matchmaking}
                        draft={matchmaking}
                        onChange={setMatchmaking}
                    />
                    <div className="details-actions">
                        <button type="button" className="btn" onClick={() => setEditing(false)}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={saving || (!rankingChanged && !matchmakingChanged)}>
                            Save methods
                        </button>
                    </div>
                </form>
            )}
        </section>
    );
}

export default AlgorithmSettings;
